import type { AvailabilityCheckAutofillReference } from '../../../types/next'
import * as next from '../../../types/next'
import { REFERENCED_TYPE } from '../../../types/next'
import type {
  AddressControlOptions,
  AddressControlOptionsDto,
  AddressControlOptionsLegacy
} from '../../../utils'
import { CONTROL_NAME, FIELD_MAPPING } from '../../../utils'
import type * as current from '../../../utils/types/sharedTypes'
import type { UpBlockHandler } from '../types'
import { findBlockIdWithStepId, newId } from '../utils'

import { upCommonSettings } from './common'

const upField = (field: any): next.CommonFieldSettings => {
  if (!field)
    return {
      display: false,
      required: false
    }

  return {
    display: true,
    required: field.required || false
  }
}

const upFields = (
  fields: Record<string, any>
): next.AddressBlock['settings']['fields'] => {
  return {
    zipCity: upField(fields.zipCity),
    suburb: upField(fields.suburb),
    streetName: upField(fields.streetName),
    houseNumber: upField(fields.houseNumber),
    extention: upField(fields.extention)
  }
}

const upUnlistedFields = (
  unlistedFields: Record<string, any>
): next.AddressBlock['settings']['unlistedFields'] => {
  return {
    zipCity: upField(unlistedFields.zipCity),
    suburb: upField(unlistedFields.suburb),
    plotArea: upField(unlistedFields.plotArea),
    plotOfLand: upField(unlistedFields.plotOfLand)
  }
}

const upReferences = (
  journey?: current.Journey,
  isInjectAddressEnabled?: boolean,
  injectAddressSettings?: { relatedBlock?: string }
) => {
  const references: Record<
    next.ReferenceId,
    AvailabilityCheckAutofillReference
  > = {}

  if (
    isInjectAddressEnabled &&
    injectAddressSettings?.relatedBlock &&
    // Journey should always be provided
    journey
  ) {
    const referenceId = newId<next.ReferenceId>()

    const [stepId, blockName] = injectAddressSettings.relatedBlock.split('/')
    const blockId = findBlockIdWithStepId(journey, stepId, blockName)

    references[referenceId] = {
      meaning: 'availability-check-autofill',
      referenceType: REFERENCED_TYPE.BLOCK,
      referenceId: blockId,
      id: referenceId
    } as AvailabilityCheckAutofillReference
  }

  return references
}

export const up: UpBlockHandler<next.AddressBlock> = (uischema, _, journey) => {
  if (uischema.type !== CONTROL_NAME.ADDRESS_BLOCK) {
    throw new Error('Expected a valid Address control')
  }

  const optionsRaw = uischema.options || {}

  const options = {
    ...mapToAddressOptions(optionsRaw as AddressControlOptionsDto<any>),

    // injection settings are not mapped to options, so we need to add them manually
    isInjectAddressEnabled: optionsRaw?.isInjectAddressEnabled,
    injectAddressSettings: optionsRaw?.injectAddressSettings
  }

  if ('error' in options) {
    throw new Error(options.error)
  }

  const supportUnlisted = Boolean(options.supportUnlisted)

  const baseSettings = {
    ...upCommonSettings(uischema),
    fields: upFields(options.fields),
    autocomplete: {
      enabled: options.countryAddressSettings?.enableAutoComplete || false,
      countryCode: options.countryAddressSettings?.countryCode,
      allowFreeText: options.countryAddressSettings?.enableFreeText
    },
    mapIntegration: {
      enabled: Boolean(
        options.googleMapsIntegrationOptions?.isGoogleMapsEnabled
      ),
      repositionAllowed: Boolean(
        options.googleMapsIntegrationOptions?.isRepositioningAllowed
      )
    }
  }

  return {
    type: next.BLOCK_TYPE.ADDRESS,
    settings: supportUnlisted
      ? {
          ...baseSettings,
          supportUnlisted: true as const,
          unlistedFields: upUnlistedFields(options.unlistedFields)
        }
      : {
          ...baseSettings,
          supportUnlisted: false as const,
          unlistedFields: undefined as never
        },
    references: upReferences(
      journey,
      options?.isInjectAddressEnabled,
      options?.injectAddressSettings
    )
  }
}

// some utils

const isLegacyDto = (obj: any): obj is AddressControlOptionsLegacy<any> =>
  obj && obj['fieldsOrder']

const isFieldInMapping = (key: any): key is keyof typeof FIELD_MAPPING => {
  if (typeof key === 'string') {
    if (key in FIELD_MAPPING) return true

    return false
  }

  return false
}

export const mapToAddressOptions = (
  // type any here is just a type of GridProps from MUI
  dto?: AddressControlOptionsDto<any>
): AddressControlOptions | { error: string } => {
  const fields: AddressControlOptions['fields'] = {}
  const unlistedFields: AddressControlOptions['unlistedFields'] = {
    zipCity: {}
  }

  if (!dto) return { error: 'Incoming DTO is not set' }

  // the following function is intercepting the flow to map the legacy dto to the new dto
  // it will also mark field as disabled if it is part of the disabledFields array
  if (!isLegacyDto(dto)) {
    if (!dto.unlistedFields) {
      return {
        ...dto,
        unlistedFields
      }
    } else if (!('zipCity' in dto.unlistedFields)) {
      return {
        ...dto,
        unlistedFields: {
          ...dto.unlistedFields,
          zipCity: {}
        }
      }
    } else {
      return dto
    }
  }

  const fieldsDto =
    dto.fieldsOrder && Array.isArray(dto.fieldsOrder) ? dto.fieldsOrder : []
  const fieldsProcessed: string[] = []

  try {
    fieldsDto.forEach((item: any) => {
      const fieldName = item.name

      if (!fieldName) throw 'DTO Mapping -> field name does not exist'

      // do not include unsused fields
      if (!isFieldInMapping(fieldName)) return

      let placeholder: string | undefined = undefined
      const key = FIELD_MAPPING[fieldName]['name']
      const keyLabel = FIELD_MAPPING[fieldName]['label']
      const keyPlaceholder = FIELD_MAPPING[fieldName]['placeholder']

      const label = dto.labels?.[keyLabel]
      const required =
        dto.requiredFields?.[key] || // if key is streetName, check also for key street
        (key === 'streetName' ? dto.requiredFields?.['street'] : undefined)
      const disabled =
        dto.disabledFields?.[key] || // if key is streetName, check also for key street
        (key === 'streetName' ? dto.disabledFields?.['street'] : undefined)

      const layout = item.gridItemProps

      placeholder = dto.placeholders
        ? dto.placeholders[keyPlaceholder]
        : undefined

      Object.assign(fields, {
        [FIELD_MAPPING[fieldName]['targetName']]: {
          ...(label && { label }),
          ...(placeholder && { placeholder }),
          ...(required && { required }),
          ...(disabled && { disabled }),
          ...(layout && { layout })
        }
      })

      // add to processed fields
      fieldsProcessed.push(fieldName)
    })
  } catch (error) {
    // if any error in mapping, return it here
    if (error instanceof Error) {
      return { error: error.message }
    } else {
      return { error: `${error}` }
    }
  }

  const countryAddressSettings =
    (dto as any).countryAddressSettings ??
    ({
      enableAutoComplete: true,
      countryCode: dto.initialAddress?.countryCode?.toUpperCase() ?? 'DE',
      enableFreeText: false
    } as AddressControlOptions['countryAddressSettings'])

  return {
    fields,
    unlistedFields,
    acceptSuggestedOnly: dto.acceptOnlySuggestedAddresses,
    countryAddressSettings: countryAddressSettings
  }
}
